import type {
  Campaign,
  Dashboard,
  InventoryAccount,
  Location,
  Me,
  Paginated,
  Run,
  RunParticipant,
  VisitInput,
  Wave,
} from './types.js';

export class ApiClientError extends Error {
  public constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

function csrfToken(): string | null {
  const cookies = document.cookie.split(';').map((cookie) => cookie.trim());
  for (const name of ['__Host-campus_csrf', 'campus_csrf']) {
    const match = cookies.find((cookie) => cookie.startsWith(`${name}=`));
    if (match) return decodeURIComponent(match.slice(name.length + 1));
  }
  return null;
}

type ApiRequestOptions = Omit<RequestInit, 'body'> & {
  body?: BodyInit | Record<string, unknown> | null;
};

export async function api<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { body: inputBody, ...requestOptions } = options;
  const method = options.method?.toUpperCase() ?? 'GET';
  const headers = new Headers(options.headers);
  let body = inputBody;
  if (
    body !== undefined &&
    body !== null &&
    typeof body === 'object' &&
    !(body instanceof FormData) &&
    !(body instanceof Blob) &&
    !(body instanceof ArrayBuffer) &&
    !(body instanceof URLSearchParams) &&
    !(body instanceof ReadableStream)
  ) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(body);
  }
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const token = csrfToken();
    if (token) headers.set('X-CSRF-Token', token);
  }
  headers.set('Accept', 'application/json');
  const requestInit: RequestInit = {
    ...requestOptions,
    method,
    headers,
    credentials: 'same-origin',
  };
  if (body !== undefined) requestInit.body = body as BodyInit | null;
  const response = await fetch(path, requestInit);
  const contentType = response.headers.get('Content-Type') ?? '';
  const payload: unknown = contentType.includes('application/json')
    ? await response.json()
    : await response.text();
  if (!response.ok) {
    const error =
      typeof payload === 'object' && payload !== null && 'error' in payload
        ? (
            payload as {
              error: { code?: string; message?: string; details?: unknown };
            }
          ).error
        : {};
    throw new ApiClientError(
      response.status,
      error.code ?? 'request_failed',
      error.message ?? `Request failed (${response.status}).`,
      error.details,
    );
  }
  return payload as T;
}

function params(input: Record<string, string | number | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(input)) {
    if (value !== null && value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  }
  return search.toString();
}

export async function allLocations(
  input: Record<string, string | number | null | undefined>,
): Promise<Paginated<Location>> {
  const pageSize = 100;
  const firstPage = await api<Paginated<Location>>(
    `/api/v1/locations?${params({ ...input, page: 1, pageSize })}`,
  );
  if (firstPage.pagination.totalPages <= 1) return firstPage;
  const remainingPages = await Promise.all(
    Array.from({ length: firstPage.pagination.totalPages - 1 }, (_, index) =>
      api<Paginated<Location>>(
        `/api/v1/locations?${params({ ...input, page: index + 2, pageSize })}`,
      ),
    ),
  );
  const data = [firstPage, ...remainingPages].flatMap((page) => page.data);
  return {
    data,
    pagination: {
      page: 1,
      pageSize: data.length,
      total: firstPage.pagination.total,
      totalPages: 1,
    },
  };
}

export const apiClient = {
  me: () => api<{ data: Me }>('/api/v1/me'),
  bootstrapStatus: () =>
    api<{ data: { bootstrapAvailable: boolean; turnstileSiteKey: string } }>(
      '/api/v1/auth/bootstrap/status',
    ),
  login: (input: Record<string, unknown>) =>
    api<{ data: { user: Me['user'] } }>('/api/v1/auth/login', {
      method: 'POST',
      body: input,
    }),
  bootstrap: (input: Record<string, unknown>, secret: string) =>
    api<{ data: { campaignId: string; inventorySetupRequired: boolean } }>(
      '/api/v1/auth/bootstrap',
      {
        method: 'POST',
        headers: { 'X-Bootstrap-Secret': secret },
        body: input,
      },
    ),
  acceptInvite: (input: Record<string, unknown>) =>
    api<{ data: { userId: string; campaignId: string } }>('/api/v1/auth/invites/accept', {
      method: 'POST',
      body: input,
    }),
  logout: () => api<{ data: { signedOut: boolean } }>('/api/v1/auth/logout', { method: 'POST' }),
  revokeAllSessions: () =>
    api<{ data: { revoked: boolean } }>('/api/v1/auth/revoke-all-sessions', {
      method: 'POST',
    }),
  campaigns: () => api<{ data: Campaign[] }>('/api/v1/campaigns'),
  campaign: (campaignId: string) =>
    api<{ data: Campaign }>(`/api/v1/campaigns/${encodeURIComponent(campaignId)}`),
  createCampaign: (input: Record<string, unknown>) =>
    api<{ data: { id: string; inventorySetupRequired: boolean } }>('/api/v1/campaigns', {
      method: 'POST',
      body: input,
    }),
  updateCampaign: (campaignId: string, input: Record<string, unknown>) =>
    api(`/api/v1/campaigns/${encodeURIComponent(campaignId)}`, {
      method: 'PATCH',
      body: input,
    }),
  initializeInventory: (campaignId: string, quantity: number) =>
    api(`/api/v1/campaigns/${encodeURIComponent(campaignId)}/inventory/initialize`, {
      method: 'POST',
      body: { quantity, confirmation: 'CONFIRM' },
    }),
  waves: (campaignId: string) => api<{ data: Wave[] }>(`/api/v1/waves?${params({ campaignId })}`),
  createWave: (input: Record<string, unknown>) =>
    api<{ data: { id: string } }>('/api/v1/waves', { method: 'POST', body: input }),
  updateWave: (waveId: string, input: Record<string, unknown>) =>
    api(`/api/v1/waves/${encodeURIComponent(waveId)}`, {
      method: 'PATCH',
      body: input,
    }),
  bulkWavePlan: (waveId: string, input: Record<string, unknown>) =>
    api(`/api/v1/waves/${encodeURIComponent(waveId)}/plans/bulk`, {
      method: 'POST',
      body: input,
    }),
  locations: (input: Record<string, string | number | null | undefined>) =>
    api<Paginated<Location>>(`/api/v1/locations?${params(input)}`),
  allLocations,
  location: (locationId: string) =>
    api<{ data: Location & { history: Array<Record<string, unknown>> } }>(
      `/api/v1/locations/${encodeURIComponent(locationId)}`,
    ),
  bulkUpdateLocations: (input: Record<string, unknown>) =>
    api<{ data: { requested: number; changed: number } }>('/api/v1/locations/bulk/update', {
      method: 'POST',
      body: input,
    }),
  runs: (campaignId: string, status?: string) =>
    api<Paginated<Run>>(`/api/v1/runs?${params({ campaignId, status, pageSize: 100 })}`),
  participants: (runId: string) =>
    api<{ data: RunParticipant[] }>(`/api/v1/runs/${encodeURIComponent(runId)}/participants`),
  createRun: (input: Record<string, unknown>) =>
    api<{ data: { id: string; inventoryAccountId: string } }>('/api/v1/runs', {
      method: 'POST',
      body: input,
    }),
  joinRun: (runId: string, userId?: string) =>
    api(`/api/v1/runs/${encodeURIComponent(runId)}/join`, {
      method: 'POST',
      body: userId ? { userId } : {},
    }),
  updateRun: (runId: string, input: Record<string, unknown>) =>
    api(`/api/v1/runs/${encodeURIComponent(runId)}`, {
      method: 'PATCH',
      body: input,
    }),
  assignRunLocations: (runId: string, locationIds: string[]) =>
    api(`/api/v1/runs/${encodeURIComponent(runId)}/assignments`, {
      method: 'POST',
      body: { locationIds },
    }),
  closeRun: (
    runId: string,
    returnedQuantity: number,
    notes?: string,
    finalStatus: 'completed' | 'cancelled' = 'completed',
  ) =>
    api(`/api/v1/runs/${encodeURIComponent(runId)}/close`, {
      method: 'POST',
      body: { returnedQuantity, finalStatus, ...(notes ? { notes } : {}) },
    }),
  recordVisit: (event: VisitInput) =>
    api<{
      data: {
        status: 'created' | 'duplicate';
        eventId: string;
        possibleDuplicateOfEventId: string | null;
        reconciliationWarning: string | null;
      };
    }>('/api/v1/visits', { method: 'POST', body: event as unknown as Record<string, unknown> }),
  syncVisits: (events: VisitInput[], ownerUserId: string) =>
    api<{
      data: {
        results: Array<
          | {
              clientIndex?: number;
              idempotencyKey: string | null;
              status: 'created' | 'duplicate';
              eventId: string;
              possibleDuplicateOfEventId: string | null;
              reconciliationWarning: string | null;
            }
          | {
              clientIndex?: number;
              idempotencyKey: string | null;
              status: 'failed';
              error: {
                code: string;
                message: string;
                retryable: boolean;
                details?: unknown;
              };
            }
        >;
      };
    }>('/api/v1/visits/batch', {
      method: 'POST',
      headers: { 'X-Offline-Owner-User-Id': ownerUserId },
      body: { events: events as unknown as Array<Record<string, unknown>> },
    }),
  dashboard: (campaignId: string, campusId?: string, buildingId?: string) =>
    api<{ data: Dashboard }>(
      `/api/v1/dashboard/summary?${params({ campaignId, campusId, buildingId })}`,
    ),
  inventoryAccounts: (campaignId: string) =>
    api<{ data: InventoryAccount[] }>(`/api/v1/inventory/accounts?${params({ campaignId })}`),
  inventoryLedger: (campaignId: string) =>
    api<Paginated<Record<string, unknown>>>(
      `/api/v1/inventory/ledger?${params({ campaignId, pageSize: 50 })}`,
    ),
  transferInventory: (input: Record<string, unknown>) =>
    api('/api/v1/inventory/transfer', { method: 'POST', body: input }),
  adjustInventory: (input: Record<string, unknown>) =>
    api('/api/v1/inventory/adjustments', { method: 'POST', body: input }),
  users: (campaignId: string) =>
    api<Paginated<Record<string, unknown>>>(
      `/api/v1/admin/users?${params({ campaignId, pageSize: 100 })}`,
    ),
  createUser: (input: Record<string, unknown>) =>
    api('/api/v1/admin/users', { method: 'POST', body: input }),
  createUserInvite: (input: Record<string, unknown>) =>
    api<{ data: { id: string; token: string; expiresAt: string; note: string } }>(
      '/api/v1/admin/users/invites',
      { method: 'POST', body: input },
    ),
  updateUser: (userId: string, input: Record<string, unknown>) =>
    api(`/api/v1/admin/users/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      body: input,
    }),
  resetUserPassword: (userId: string, input: Record<string, unknown>) =>
    api(`/api/v1/admin/users/${encodeURIComponent(userId)}/reset-password`, {
      method: 'POST',
      body: input,
    }),
  imports: (campaignId: string) =>
    api<{ data: Array<Record<string, unknown>> }>(
      `/api/v1/admin/imports?${params({ campaignId })}`,
    ),
  importDetail: (importId: string) =>
    api<{ data: Record<string, unknown> }>(
      `/api/v1/admin/imports/${encodeURIComponent(importId)}?pageSize=100`,
    ),
};
